import React, { useEffect, useState } from 'react'
import { endOfDay, formatISO, startOfDay } from 'date-fns'
import { Select, Typography } from '@telavita-core/react-design-kit'

import { useGetProfessionalAvailabilities } from 'hooks'
import { SelectAvailableDateOnWeeklyCalendar } from 'components/SelectAvailableDateOnWeeklyCalendar'

import { 
  OptionData, 
  formatDateForRestriction, 
  formatDateTime, 
  formatSelectOptions, 
  handleSelectHour 
} from 'utils/reservationHandlers'

import * as S from './styles'

interface IOptionData {
  id: string | number
  label: string
}

interface AvailabilityList {
  [date: string]: string[]
}

interface SelectAvailableProfessionalScheduleProps {
  availabilityList: AvailabilityList
  handleDate: (date: string) => void
  patientUsername: string
  professionalUsername: string
  defaultSelectedTime?: IOptionData
}

const noAvailabilityMessage = [
  'Nenhum horário.', 
  <br key='1'/>, 
  'Mude a data no', 
  <br key='2'/>, 
  'campo ao lado.'
]

export const SelectAvailableProfessionalSchedule: React.FC<SelectAvailableProfessionalScheduleProps> = ({ 
  availabilityList, 
  handleDate,
  patientUsername,
  professionalUsername,
  defaultSelectedTime = null,
}) => {
  const availableDate = Object.keys(availabilityList)[0]
  const availableHours = Object.values(availabilityList)[0] 
  
  const [selectedHour, setSelectedHour] = useState<IOptionData>(defaultSelectedTime)
  const [selectedDate, setSelectedDate] = useState(() => formatDateForRestriction(availableDate))
  
  const [availabilityHours, setAvailabilityHours] = useState(availableHours)
  const [shouldRefetchData, setShouldRefetchData] = useState(false)

  const formattedRestriction = [
    formatISO(startOfDay(selectedDate)),
    formatISO(endOfDay(selectedDate)),
  ]

  const { data, isFetching, refetch } = useGetProfessionalAvailabilities({ 
    restrictions: formattedRestriction,
    patientUsername,
    professionalUsername
  })

  function handleDateSelection(date: Date) {
    setAvailabilityHours([])
    setSelectedDate(date)
    setSelectedHour(null)
    setShouldRefetchData(true)
  }

  useEffect(() => {
    if (data) {
      const newData = data[0]

      setAvailabilityHours(Object.values(newData)[0])
      setShouldRefetchData(false)
    }
  }, [data])

  useEffect(() => {
    if (shouldRefetchData) {
      refetch({
        exact: true,
      })
    }
  }, [refetch, shouldRefetchData])
  
  useEffect(() => {
    if (!selectedHour) {
      handleDate(null)
      return
    }

    const formattedDate = formatDateTime(selectedDate, selectedHour)
    handleDate(formattedDate)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHour, selectedDate])

  const selectTimeOptions = formatSelectOptions(availabilityHours, noAvailabilityMessage) as OptionData[]

  return (
    <S.Container>
      <SelectAvailableDateOnWeeklyCalendar
        selectedDate={selectedDate} 
        onSetSelectedDate={handleDateSelection} 
      />
      <Typography variant='content2'> às </Typography>
      <Select
        options={selectTimeOptions}
        selected={selectedHour}
        onSelect={(hour) => setSelectedHour(handleSelectHour(hour))}
        label=''
        customClassName='AvailabilitySelect'
        placeholder={isFetching ? 'Carregando...' : 'Selecione'}
      />
    </S.Container>
  )
}